"use client";

import { clsx } from "clsx";
import { AnimatePresence, MotionConfig, Transition } from "motion/react";
import * as m from "motion/react-m";
import Image from "next/image";
import { Tabs, TabPanel, type Key } from "react-aria-components";
import type { MosaicSwitcherBlock } from "@mw/shared/payload-types";
import { getImageProps, getLinkProps } from "@utilities/payload";
import { easedGradientStops } from "@ui/lib/gradients";
import { TabList } from "@ui/components/core/TabList";
import { useRef, useState } from "react";
import { sizes } from "@ui/lib/sizes";
import { pxToRem } from "@ui/lib/utils";
import { BaseLink } from "@ui/components/core/Link";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

const MotionBaseLink = m.create(BaseLink);

type MosaicGroup = MosaicSwitcherBlock["groups"][number];
type MosaicItem = MosaicGroup["items"][number];

const TRANSITION: Transition = { duration: 0.8, ease: [0.8, 0, 0.2, 1] };
const STAGGER_DELAY = 0.1;
const BORDER_RADIUS = 20;

function trackMosaicClick(item: MosaicItem, groupTitle: string, itemPosition: number, href: string) {
  captureEvent(
    EventName.mw_mosaic_item_clicked,
    { item_title: item.title, group_title: groupTitle, item_position: itemPosition, link_href: href },
    { transport: "sendBeacon" },
  );
}

function MosaicItemPlaceholder({ isLastItem = false }: { isLastItem?: boolean }) {
  return (
    <div
      style={{ borderRadius: BORDER_RADIUS }}
      className={clsx(
        "tw-isolate tw-relative tw-aspect-square tw-flex tw-items-end tw-overflow-hidden",
        isLastItem
          ? "tw-flex-auto tw-w-0 xl:tw-h-0 xl:tw-w-auto"
          : "tw-flex-auto tw-w-0 md:tw-w-auto md:tw-h-0 tw-mt-auto xl:tw-ml-auto",
      )}
    >
      <div
        className={clsx(
          "tw-absolute -tw-z-10 tw-bg-cream tw-left-0 tw-top-0 tw-aspect-square",
          isLastItem ? "tw-w-full xl:tw-h-full xl:tw-w-auto" : "tw-h-full",
        )}
        style={{ borderRadius: BORDER_RADIUS }}
      />
    </div>
  );
}

function MosaicItem({
  item,
  isActive,
  layoutId,
  delay = 0,
  isLastItem = false,
  groupTitle,
  itemIndex,
}: {
  item: MosaicItem;
  isActive: boolean;
  layoutId: string;
  delay?: number;
  isLastItem?: boolean;
  groupTitle: string;
  itemIndex: number;
}) {
  const transition = { ...TRANSITION, delay };
  const linkProps = getLinkProps(item.link);
  const imageProps = getImageProps(item.image, { placeholder: true, fill: true });
  const logoImageProps = getImageProps(item.logo);
  const Component = linkProps ? MotionBaseLink : m.div;

  return (
    <Component
      {...(linkProps ?? {})}
      layoutId={layoutId}
      transition={transition}
      style={{ borderRadius: BORDER_RADIUS }}
      inert={!isActive}
      onClick={linkProps ? () => trackMosaicClick(item, groupTitle, itemIndex, String(linkProps.href)) : undefined}
      className={clsx(
        "tw-isolate tw-relative tw-aspect-square tw-flex tw-flex-col tw-justify-end tw-overflow-hidden",
        !isLastItem && "tw-mt-auto xl:tw-ml-auto",
        linkProps &&
          "hover:tw-shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1),0_0_0_4px_theme(colors.cream)] tw-transition-shadow focus-visible:tw-outline-focus",
        isActive
          ? isLastItem
            ? "tw-w-3/4 sm:tw-w-2/3 md:tw-w-[calc((100%-0.5rem)*2/3)] md:tw-h-full xl:tw-w-full xl:tw-h-auto"
            : "tw-w-3/4 sm:tw-w-2/3 md:tw-w-full"
          : isLastItem
            ? "tw-flex-auto tw-w-0 xl:tw-h-0 xl:tw-w-auto"
            : "tw-flex-auto tw-w-0 md:tw-w-auto md:tw-h-0",
      )}
    >
      <m.div
        layoutId={layoutId + "-bg"}
        className={clsx(
          "tw-absolute -tw-z-10 tw-bg-cream tw-rounded-2.5xl tw-left-0 tw-top-0 tw-aspect-square",
          isLastItem ? "tw-w-full xl:tw-h-full xl:tw-w-auto" : "tw-h-full",
        )}
        transition={transition}
        style={{ borderRadius: BORDER_RADIUS }}
      />
      <m.div
        layoutId={layoutId + "-image"}
        className={clsx(
          "tw-absolute tw-left-0 tw-top-0 tw-aspect-square -tw-z-10 tw-overflow-hidden",
          isLastItem ? "tw-w-full xl:tw-h-full xl:tw-w-auto" : "tw-h-full",
        )}
        style={{ opacity: isActive ? 1 : 0, borderRadius: BORDER_RADIUS }}
        transition={transition}
      >
        {imageProps && (
          <Image {...imageProps} sizes={sizes("60vw", { sm: pxToRem(320) })} alt='' className='tw-object-cover' />
        )}
      </m.div>
      {logoImageProps && (
        <m.div
          layoutId={layoutId + "-logo"}
          transition={transition}
          style={{ opacity: isActive ? 1 : 0 }}
          className='tw-flex-none tw-mb-auto tw-w-max tw-pt-4 sm:tw-pt-6 tw-px-4 sm:tw-px-6'
        >
          <Image {...logoImageProps} alt='' className='tw-w-auto tw-h-8 sm:tw-h-10 tw-brightness-0 tw-invert' />
        </m.div>
      )}
      <m.div
        layoutId={layoutId + "-text"}
        className={clsx(
          "tw-absolute tw-left-0 tw-top-0 tw-aspect-square tw-overflow-hidden",
          isLastItem ? "tw-w-full xl:tw-h-full xl:tw-w-auto" : "tw-h-full",
        )}
        transition={transition}
        style={{ opacity: isActive ? 1 : 0, borderRadius: BORDER_RADIUS }}
      >
        <m.div
          layout
          transition={transition}
          className='tw-absolute tw-top-0 tw-left-0 tw-aspect-square tw-flex tw-items-end tw-w-[calc((100cqw-1rem)*3/4)] sm:tw-w-[calc((100cqw-1rem)*2/3)] md:tw-w-[calc(40cqw-1rem)] xl:tw-w-[calc(100cqw)] tw-px-4 tw-pb-6 sm:tw-px-6 sm:tw-pb-7'
        >
          <div className='tw-relative tw-isolate tw-text-white tw-type-base'>
            {item.title}
            <div
              className='tw-absolute -tw-z-10 tw-inset-0 -tw-inset-x-8 -tw-inset-y-6 sm:-tw-inset-y-7'
              style={{
                background: `linear-gradient(to top, ${easedGradientStops({ color: "rgb(0 0 0 / 0.5)", at: 50 }, "rgb(0 0 0 / 0)")})`,
              }}
            />
          </div>
        </m.div>
      </m.div>
      <m.div
        layoutId={layoutId + "-ring"}
        className={clsx(
          "tw-absolute tw-left-0 tw-top-0 tw-aspect-square tw-pointer-events-none",
          isLastItem ? "tw-w-full xl:tw-h-full xl:tw-w-auto" : "tw-h-full",
        )}
        style={{
          borderRadius: BORDER_RADIUS,
          boxShadow: "0 0 0 1px inset rgb(0 0 0 / 0.1)",
          opacity: isActive ? 1 : 0,
        }}
        transition={transition}
      />
    </Component>
  );
}

function MosaicGroup({ group, groups }: { group: MosaicGroup; groups: Array<MosaicGroup> }) {
  const groupIndex = groups.findIndex(g => g === group);
  const groupsReversed = [groups[1], groups[0]].filter(Boolean);

  return (
    <div className='tw-flow-root'>
      <div className='tw-max-w-[30rem] md:tw-max-w-[51.75rem] tw-mx-auto xl:tw-max-w-[71.5rem] -tw-my-2'>
        <div className='tw-grid xl:tw-py-2 tw-grid-cols-1 tw-grid-rows-3 md:tw-flex tw-flex-wrap xl:tw-flex-nowrap -tw-mx-2 md:tw-aspect-square xl:tw-aspect-auto tw-outline-none focus-visible:tw-outline-focus tw-rounded-2.5xl [container-type:inline-size] xl:[container-type:normal]'>
          <div className='tw-contents xl:tw-contents tw-py-2 md:tw-flex tw-w-full tw-flex-none tw-h-[60%]'>
            <div className='tw-py-2 md:tw-py-0 tw-w-full md:tw-w-[40%] xl:tw-w-[20.75rem] tw-px-2 tw-flex-none tw-flex md:tw-flex-col-reverse xl:tw-flex-col tw-gap-4 xl:[container-type:inline-size]'>
              {groups.map(g => (
                <MosaicItem
                  key={g.title}
                  item={g.items[1]}
                  isActive={g.title === group.title}
                  layoutId={`${g.title}-0`}
                  groupTitle={group.title}
                  itemIndex={1}
                />
              ))}
              {groups.length === 1 && <MosaicItemPlaceholder />}
            </div>
            <div className='tw-py-2 md:tw-py-0 tw-w-full md:tw-order-first xl:tw-order-none md:tw-w-[60%] tw-px-2 tw-flex-none xl:tw-flex-auto'>
              <div className='tw-relative tw-h-full tw-w-full md:tw-aspect-square'>
                {groupsReversed.map((g, gIndex) => {
                  const linkProps = getLinkProps(g.items[0].link);
                  const imageProps = getImageProps(g.items[0].image, { placeholder: true, fill: true });
                  const logoImageProps = getImageProps(g.items[0].logo);
                  const Component = linkProps ? MotionBaseLink : m.div;

                  return (
                    <Component
                      key={g.title}
                      {...(linkProps ?? {})}
                      layoutId={`${g.title}-1`}
                      onClick={
                        linkProps
                          ? () => trackMosaicClick(g.items[0], group.title, 0, String(linkProps.href))
                          : undefined
                      }
                      className={clsx(
                        "tw-absolute tw-isolate tw-inset-0 tw-size-full tw-px-4 tw-pt-4 sm:tw-pt-6 tw-pb-6 sm:tw-px-6 sm:tw-pb-7 tw-rounded-2.5xl tw-overflow-hidden tw-flex tw-flex-col tw-items-start tw-justify-end",
                        linkProps &&
                          "focus-visible:tw-outline-focus hover:tw-shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1),0_0_0_4px_theme(colors.cream)] tw-transition-shadow",
                      )}
                      style={{ opacity: groupIndex === 0 ? 1 : gIndex === 0 ? 1 : 0 }}
                      transition={{ ...TRANSITION, delay: STAGGER_DELAY }}
                      inert={group.title !== g.title}
                    >
                      {imageProps && (
                        <Image
                          {...imageProps}
                          sizes={sizes("100vw", { sm: pxToRem(490) })}
                          alt=''
                          className='-tw-z-10 tw-object-cover'
                        />
                      )}
                      {logoImageProps && (
                        <Image
                          {...logoImageProps}
                          alt=''
                          className='tw-w-auto tw-h-8 sm:tw-h-10 tw-brightness-0 tw-invert tw-mb-auto'
                        />
                      )}
                      <div className='tw-relative tw-isolate tw-text-white tw-type-base tw-text-pretty tw-w-full'>
                        <div
                          className='tw-absolute -tw-z-10 tw-inset-0 -tw-inset-x-8 -tw-inset-y-6 sm:-tw-inset-y-7'
                          style={{
                            background: `linear-gradient(to top, ${easedGradientStops({ color: "rgb(0 0 0 / 0.5)", at: 50 }, "rgb(0 0 0 / 0)")})`,
                          }}
                        />
                        {g.items[0].title}
                      </div>
                      <div className='tw-absolute tw-inset-0 tw-ring-1 tw-rounded-2.5xl tw-ring-inset tw-ring-black/10 tw-pointer-events-none' />
                    </Component>
                  );
                })}
              </div>
            </div>
          </div>
          <div className='tw-w-full md:tw-w-[60%] md:tw-h-[40%] tw-py-2 tw-px-2 tw-mx-auto xl:tw-w-[20.75rem] tw-flex-none tw-flex md:tw-flex-row-reverse xl:tw-flex-col tw-gap-4 xl:tw-h-auto xl:tw-py-0 xl:[container-type:inline-size]'>
            {groups.length === 1 && <MosaicItemPlaceholder isLastItem />}
            {groupsReversed.map(g => (
              <MosaicItem
                key={g.title}
                item={g.items[2]}
                delay={STAGGER_DELAY * 2}
                isActive={g.title === group.title}
                layoutId={`${g.title}-2`}
                isLastItem
                groupTitle={group.title}
                itemIndex={2}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function BlockMosaicSwitcher(props: MosaicSwitcherBlock) {
  const groups = props.groups;
  const [selectedKey, setSelectedKey] = useState<Key>(groups?.[0]?.title);
  const lastSwitchRef = useRef<number>(-Infinity);

  if (!groups) return null;
  if (groups.length < 1 || groups.length > 2) return null;

  if (groups.length === 1) {
    return <MosaicGroup group={groups[0]} groups={groups} />;
  }

  return (
    <Tabs
      selectedKey={selectedKey}
      onSelectionChange={key => {
        // Temporary fix to prevent the tabs from switching too quickly and causing animation jank that was introduced when React Aria was upgraded in #2161
        const now = window.performance.now();
        if (now - lastSwitchRef.current > 1100) {
          setSelectedKey(key);
          lastSwitchRef.current = now;
        }
      }}
    >
      <div className='tw-mb-12'>
        <TabList
          items={groups.map(group => ({
            id: group.title,
            title: group.title,
            shortTitle: group.shortTitle ?? undefined,
          }))}
          aria-label={props.title}
          className='tw-mx-auto'
          equalWidth
        />
      </div>
      <MotionConfig reducedMotion='user'>
        <AnimatePresence mode='popLayout'>
          {groups.map(group =>
            selectedKey === group.title ? (
              // Prevents scroll anchoring when popLayout sets exiting panel to position: absolute
              <TabPanel key={group.title} id={group.title} shouldForceMount style={{ overflowAnchor: "none" }}>
                <MosaicGroup group={group} groups={groups} />
              </TabPanel>
            ) : null,
          )}
        </AnimatePresence>
      </MotionConfig>
    </Tabs>
  );
}
